import { ALL_DATES, DATE_RANGE_PRESET_GROUPS, dateFilterSummary, presetToFilterValue } from '@utils/cbDateSettings'
import { filtersAsArray } from '@utils/reportFilterWire'

/**
 * The report's CURRENT date preset, derived from its filter values (spec 120 §7).
 *
 * ## Why this exists at all
 *
 * `rollForward` is the single most expensive field this modal writes. ON, it
 * stores a preset TOKEN that cb-api re-resolves at every firing; OFF, it stores
 * `'pinned'` and the run keeps re-reporting the window the schedule was created
 * in. Until now the create modal hardcoded `'lastMonth'`
 * (`QueueReportModal.jsx:92`), so a schedule built from a This-Month report
 * reported on LAST month — for ever, with a preview that agreed with itself
 * because the preview resolves the same wrong token. Nothing errored: an
 * unknown or wrong token is stored verbatim (`schedules.js:60-68`) and, at fire
 * time, a token that will not resolve simply leaves the filter untouched
 * (`recurrence.js:326-327`).
 *
 * Himaad's mock settles the semantics: the toggle's helper reads "Each run
 * reports on **This month** as of the day it fires", and `This month` is the
 * preset on the report's own filter pill. So ON must mean *this report's*
 * preset, and it has to be read off the filters rather than assumed.
 *
 * ## Why the preset is DERIVED and not stored
 *
 * Nothing anywhere stores a preset key — not `filterValues`, not
 * `preferencesStore`, not the definition. A date filter is only ever a resolved
 * wire value (`"YYYY-MM-DD|YYYY-MM-DD"`, `$$cb_all`, or a bare `|`). The only
 * mechanism that recovers the key is a reverse match: resolve every preset as
 * of NOW and see which one produces exactly this value. That is what
 * `ReportFilterPanel.jsx:243-251` and `DatePresetSelect.jsx:43-46` already do
 * for their own selects.
 *
 * This is therefore a THIRD copy of that loop, and that is a deliberate,
 * declared cost: the natural home is `src/utils/cbDateSettings.js` beside
 * `presetToFilterValue`, but this round's file partition gives that file to
 * another stream, and a fourth concurrent edit to it is how two spec numbers and
 * one financial approval path have already collided here. Consolidating all
 * three into one exported helper is filed as a follow-up in spec 120.
 *
 * ## Three refusals that are the point of the module
 *
 * 1. **No match ⇒ `null`.** An explicit custom range matches no preset, so
 *    there is no preset to name; the caller must hide the toggle and pin. A
 *    toggle whose label cannot say what it will do is worse than no toggle.
 * 2. **Two date filters that disagree ⇒ `null`.** cb-api stamps the token onto
 *    EVERY date-shaped filter and reads it back as the first one found
 *    (`schedules.js:60-78`), so one token cannot express two different presets.
 *    Naming one of them would be a claim about the other.
 * 3. **More than one preset matching ⇒ `null`.** Presets ALIAS. Two of them can
 *    resolve to the same bytes today and to different windows next month, and
 *    cb-api re-resolves the STORED token at every firing — so picking one of a
 *    tie is not a harmless tie-break, it silently renames the window.
 *
 *    This is measured, not theoretical. Driving all 25 presets through this
 *    module for all 366 days of 2027 and taking the first list-order match — the
 *    behaviour this refusal replaces — mis-derives on eleven collision classes:
 *
 *      qtd -> mtd            120 days/yr   (the first month of every quarter)
 *      ytd -> qtd             59           (February)
 *      ytd -> mtd             31           (January)
 *      today -> mtd           13           (the 1st of a month)
 *      mtd -> thisMonth       12           (a month's last day)
 *      qtd -> thisMonth        4
 *      thisQuarter -> qtd      4
 *      ytd -> thisMonth / thisWeek -> mtd / thisYear -> ytd /
 *      lastYearToDate -> lastYear                1 each
 *
 *    So a Year-to-Date schedule created any time in Q1 stored `mtd` or `qtd`,
 *    and from the next month on it emailed one month of a year's revenue with a
 *    201 at create, a success at every run, and a schedules row reporting the
 *    substituted token back as though it were chosen.
 *
 *    Probing a future date does NOT rescue this. It can DETECT that the tie
 *    matters, but both candidates remain equally good explanations of today's
 *    bytes — nothing stores which one the user picked — so a probe would still
 *    be choosing. The honest answer is to stop and say so, which is what
 *    `reportDatePresetChoices` is for: it hands the tied candidates to the UI so
 *    the USER names the window, and until they do the token is `'pinned'`.
 *
 * Every match is resolved against TODAY (`presetToFilterValue` defaults to
 * `dayjs()`), so any test over this must freeze the clock — and a test that
 * freezes it to a date with no collisions (2026-08-18 has none) proves nothing
 * about refusal 3.
 *
 * ## The known limitation, stated rather than hidden
 *
 * A reverse match cannot tell "the user picked This Month" from "the user typed
 * a custom range that happens to equal this month's". Both are the same bytes,
 * and nothing stores the difference. So a hand-typed 08/01–08/31 offers a
 * rolling toggle, defaulted ON, and next month the schedule reports September.
 *
 * That is accepted deliberately, for two reasons. It is not a silent guess: the
 * toggle's helper names the preset out loud — "Each run reports on This Month
 * as of the day it fires" — so the user is told exactly what will happen and
 * has the switch to refuse it, which is the opposite of the `'lastMonth'`
 * default this replaces. And for a RECURRING report the rolling reading is
 * almost always the intended one; pinning is the pathological case, since a
 * pinned schedule re-reports one frozen window for ever while looking healthy.
 * Recorded in spec 120 §7 as a limitation, not fixed here — the only real fix
 * is persisting the preset key alongside the resolved value, which is a change
 * to the filter model itself.
 */

/** Preset key -> its human label, from the one list the report's own select uses. */
const PRESET_LABEL_BY_KEY = new Map(
  DATE_RANGE_PRESET_GROUPS.flatMap((g) => g.presets).map((p) => [p.key, p.label])
)

export const presetLabel = (key) => PRESET_LABEL_BY_KEY.get(key) || null

/**
 * EVERY preset whose resolution equals `value` right now, in the report select's
 * own order. Empty when none does.
 *
 * The list, not the first entry, is the honest primitive — see refusal 3 in the
 * header. Callers that need one answer must decide what to do with a tie; they
 * may not silently take `[0]`.
 *
 * Unlike `ReportFilterPanel`'s copy this does NOT special-case All Dates.
 * `'all'` is a legal roll-forward token (`recurrence.js:255`) and resolving it
 * every run yields the same window every run, so allowing it is honest and
 * costs nothing. Note a proc-based report spells All Dates as a bare `'|'`
 * (`reportFilterWire.js:128`), which matches no preset and correctly falls
 * through to null / pinned — the same run either way.
 */
export const presetKeysForValue = (value) => {
  if (!value) return []
  const v = String(value)
  const keys = []
  for (const g of DATE_RANGE_PRESET_GROUPS) {
    for (const p of g.presets) {
      if (presetToFilterValue(p.key) === v) keys.push(p.key)
    }
  }
  return keys
}

/**
 * Refusal 3, as ONE function. `[k] -> k`; `[]` and `[a, b, …] -> null`.
 *
 * Every caller that needs a single answer goes through this, deliberately: the
 * refusal was written twice in the first pass of the fix — once here and once
 * inside `reportDatePreset` — and two copies of a guard is how one of them keeps
 * refusing while the other quietly starts ranking again. A mutation to this line
 * must fail both the module's tests AND the modal's.
 */
const uniqueKey = (keys) => (keys.length === 1 ? keys[0] : null)

/**
 * The ONE preset key that resolves to `value` right now, or null.
 *
 * Null covers both "no preset produces this" and "more than one does". The
 * second used to return the first list-order match, which is the blocker
 * documented in the header: `mtd` sits ahead of `qtd`, `ytd` and `today` in the
 * Common group, so it stood in for all three on 175 days a year.
 */
export const presetKeyForValue = (value) => uniqueKey(presetKeysForValue(value))

/** The filterable DATE columns that actually carry a value. */
const valuedDateColumns = (columns, values) =>
  (columns || [])
    .filter((c) => c && c.filterTypeName === 'DATE')
    .map((c) => ({ col: c, value: values?.[c.columnId] }))
    .filter((e) => e.value != null && e.value !== '')

/**
 * `{ value, keys }` — the one wire value every valued DATE column carries, and
 * every preset that resolves to it. Null when refusal 1 or 2 applies.
 *
 * Refusal 2 is enforced on the VALUE, not on the recovered key, and the two are
 * equivalent because a preset resolves to exactly one value: equal values give
 * equal candidate sets, unequal values give disjoint ones. Testing the value
 * also keeps the refusal intact for two columns that both hold custom ranges,
 * where there is no key to compare.
 */
const reportDateCandidates = (columns, values) => {
  const dated = valuedDateColumns(columns, values)
  if (dated.length === 0) return null

  const distinct = new Set(dated.map((e) => String(e.value)))
  if (distinct.size > 1) return null

  const value = String(dated[0].value)
  const keys = presetKeysForValue(value)
  if (keys.length === 0) return null
  return { value, keys }
}

/**
 * `{ key, label, value }` for the report's date preset, or null when there is
 * none, none that resolves, more than one that disagree, or more than one preset
 * that the value could equally be. See the three refusals in the header.
 */
export const reportDatePreset = (columns, values) => {
  const c = reportDateCandidates(columns, values)
  if (!c) return null
  // Through `uniqueKey`, never a second copy of the same comparison.
  const key = uniqueKey(c.keys)
  if (!key) return null
  return { key, label: presetLabel(key), value: c.value }
}

/**
 * The TIED presets, when the report's date value is one that several of them
 * produce today — `{ value, options: [{ key, label }, …] }`, or null.
 *
 * This is refusal 3's escape hatch and the reason the refusal costs no
 * capability. On 2027-01-14 a Year-to-Date report's `2027-01-01|2027-01-14` is
 * equally Month to Date, Quarter to Date and Year to Date; the UI lists exactly
 * those three and the user names one. Until they do, the emitted token is
 * `'pinned'` — a schedule whose window does not move is wrong, but it is
 * VISIBLY wrong and says so, where a first-match alias is invisible at every
 * layer.
 *
 * Never non-null at the same time as `reportDatePreset`: one preset means there
 * is nothing to disambiguate, and both being set is how a UI ends up drawing
 * two controls for one field.
 */
export const reportDatePresetChoices = (columns, values) => {
  const c = reportDateCandidates(columns, values)
  // The complement of `uniqueKey`, expressed against it rather than beside it,
  // so the never-both-non-null invariant holds by construction.
  if (!c || uniqueKey(c.keys)) return null
  return { value: c.value, options: c.keys.map((k) => ({ key: k, label: presetLabel(k) })) }
}

/**
 * EVERY resolved date window, for the "It will run with" card — display only.
 * `[{ columnId, label, summary }, …]`, empty when no DATE column carries a value.
 *
 * Plural, and that is a correctness fix rather than a generalisation. The card
 * used to name `dated[0]` alone while the residual count subtracted ALL the
 * dated columns, so a report with two valued date filters named August, hid the
 * July constraint on the other column, and suppressed the "· N other filters"
 * clause that was the only remaining hint that anything was unnamed. On a
 * financial scoping summary that is not an omission, it is a false statement of
 * what narrows the figures — the exact class this module's counts exist to
 * close. Naming all of them is what makes `wireCount - datedCount` true.
 *
 * All Dates has TWO wire spellings and they are not interchangeable, so the
 * sentinel guard comes FIRST and stays exact: a bare `'|'` handed to a range
 * formatter prints a separator with nothing either side. Everything else goes
 * through `dateFilterSummary`, which is the ONE producer behind every date pill
 * in the product, so this card and the report's own filter bar cannot drift.
 */
export const reportDateSummaries = (columns, values) =>
  valuedDateColumns(columns, values).map(({ col, value }) => {
    const v = String(value)
    return {
      columnId: col.columnId,
      label: col.label || '',
      summary: v === ALL_DATES || v === '|' ? 'All dates' : dateFilterSummary(v),
    }
  })

/**
 * The FIRST resolved date window, or null. Kept for the single-window case,
 * which is what the card prints unchanged; anything that has to be honest about
 * a report with two date filters must use `reportDateSummaries`.
 */
export const reportDateSummary = (columns, values) =>
  reportDateSummaries(columns, values)[0]?.summary ?? null

/** How many DATE columns carry a value — the card names ALL of these and counts what is left. */
export const datedColumnCount = (columns, values) => valuedDateColumns(columns, values).length

/**
 * How many filters this run will actually send to CB.
 *
 * The card's "· N other filters" clause counts from this, NOT from
 * `columns.length`, and the distinction is the difference between an
 * observation and a claim: `columns` is every column the report COULD be
 * filtered on, so on a report with eight filterable columns where only the date
 * is set, `columns.length - dated` says "7 other filters" and asserts seven
 * constraints that do not exist. On a financial scoping summary a count is a
 * claim about what narrows the figures, so it must count what is on the wire.
 *
 * Counted by running the REAL producer (`filtersAsArray`) rather than by
 * re-deciding what "set" means. It already drops empty values, and it also
 * drops several set-but-vacuous ones a naive check would count — a TAGMANAGER
 * or LOOKUP holding only an All sentinel, a CHECKBOX with nothing ticked. A
 * second definition of "set" beside it would agree today and drift; this one
 * cannot, because it IS the definition. `reportType` only changes the SPELLING
 * of All Dates, never whether a key is emitted, so the count is unaffected by
 * not knowing it here.
 */
export const wireFilterCount = (columns, values) =>
  filtersAsArray(values || {}, columns || []).length
